import { inject, Injectable, InjectionToken, Provider, Type } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { FieldType, FormFieldConfig } from './models/form-wizard.models';

export interface FormWizardCustomFieldAdapter {
  field: FormFieldConfig;
  form: FormGroup;
}

export interface FormWizardFieldAdapterRegistration {
  fieldType: FieldType;
  component: Type<FormWizardCustomFieldAdapter>;
}

export const FORM_WIZARD_FIELD_ADAPTERS =
  new InjectionToken<FormWizardFieldAdapterRegistration[]>('FORM_WIZARD_FIELD_ADAPTERS');

export function provideFormWizardFieldAdapter(
  fieldType: FieldType,
  component: Type<FormWizardCustomFieldAdapter>
): Provider {
  return {
    provide: FORM_WIZARD_FIELD_ADAPTERS,
    multi: true,
    useValue: {
      fieldType,
      component,
    } satisfies FormWizardFieldAdapterRegistration,
  };
}

@Injectable()
export class FormWizardFieldRegistryService {
  private readonly parentRegistrations =
    inject(FORM_WIZARD_FIELD_ADAPTERS, { optional: true, skipSelf: true }) ?? [];
  private readonly localRegistrations =
    inject(FORM_WIZARD_FIELD_ADAPTERS, { optional: true, self: true }) ?? [];
  private readonly registrations = [
    ...this.parentRegistrations,
    ...this.localRegistrations,
  ];
  private readonly registry = this.buildRegistry(this.registrations);

  resolve(fieldType: FieldType): Type<FormWizardCustomFieldAdapter> | null {
    return this.registry.get(fieldType) ?? null;
  }

  private buildRegistry(
    registrations: FormWizardFieldAdapterRegistration[]
  ): Map<FieldType, Type<FormWizardCustomFieldAdapter>> {
    const registry = new Map<FieldType, Type<FormWizardCustomFieldAdapter>>();

    for (const registration of registrations) {
      if (registry.has(registration.fieldType)) {
        throw new Error(
          `[FormWizard] Duplicate field adapter registration for "${registration.fieldType}".`
        );
      }

      registry.set(registration.fieldType, registration.component);
    }

    return registry;
  }
}
